<template>
  <DataTableServer
    @click:row="handleFormRowClick"
    @update:page="handlePageChange"
    :headers="headers"
    :hide-default-footer="totalItemCount === 0"
    :items="forms"
    :items-length="totalItemCount"
    :items-per-page="pageSize"
    :loading="processing"
    :page="page"
    no-data-text="No forms found"
    border
    disable-sort
    hover
  >
    <template #[`item.id`]="{ item: form }">
      {{ shortId(form.id) }}
    </template>

    <template #[`item.name`]="{ item: form }">
      <span :data-testid="`license-form-name-${form.id}`">
        {{ form.name }}
      </span>
    </template>

    <template #[`item.status`]="{ item: form }">
      <StandardChip
        v-if="form.status"
        :data-testid="`license-form-status-${form.id}`"
        :text="form.status"
        role="status"
        capitalize
      />
    </template>

    <template #[`item.submitted_at`]="{ item: form }">
      <LongDate
        :date="form.submitted_at"
        short-month
      />
    </template>

    <template #[`item.updated_at`]="{ item: form }">
      <LongDate
        :date="form.updated_at"
        short-month
      />
    </template>
  </DataTableServer>
</template>

<script setup>
import { useRouter } from 'vue-router';

import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import LongDate from '@/shared/components/LongDate.vue';
import { shortId } from '@/plugins/filters';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';

const { isManager } = useRole();
const router = useRouter();
const { scopedApi } = useApi();

const headers = [
  { title: 'ID', value: 'id', sortable: false },
  { title: 'Name', value: 'name', sortable: false },
  { title: 'Status', value: 'status', align: 'center', sortable: false },
  { title: 'Submitted', value: 'submitted_at', sortable: false },
  { title: 'Updated', value: 'updated_at', sortable: false },
];

const forms = ref([]);
const page = ref(1);
const pageSize = ref(10);
const processing = ref(false);
const totalItemCount = ref(0);

const props = defineProps({
  licenseId: {
    type: String,
    required: true,
  },
});

async function handleFormRowClick(_event, { item: form }) {
  if (!router.hasRoute('LicenseFormNew')) return;
  await router.push({
    name: 'LicenseFormNew',
    params: { licenseId: props.licenseId, formId: form.id },
  });
}

function handlePageChange(newPage) {
  page.value = newPage;
  void loadForms();
}

function load() {
  page.value = 1;
  void loadForms();
}

async function loadForms() {
  processing.value = true;
  try {
    if (isManager) {
      await loadManagerForms();
    } else {
      await loadSpecialistForms();
    }
  } finally {
    processing.value = false;
  }
}

async function loadManagerForms() {
  const response = await scopedApi.form.index({
    'filter[license]': props.licenseId,
    'page[offset]': (page.value - 1) * pageSize.value,
    'page[limit]': pageSize.value,
  });
  const jsonApiBody = response?.data;
  const jsonApiData = jsonApiBody?.data;

  if (!jsonApiData) {
    forms.value = [];
    totalItemCount.value = 0;
    return;
  }

  forms.value = jsonApiData.map((item) => ({
    id: item.id,
    ...item.attributes,
    schema_id: item.relationships?.schema?.data?.id,
  }));

  const parsedTotalCount = parseInt(jsonApiBody.meta?.total_count, 10);
  const parsedPageLimit = parseInt(jsonApiBody.meta?.page_limit, 10);

  totalItemCount.value = Number.isNaN(parsedTotalCount) ? forms.value.length : parsedTotalCount;

  if (!Number.isNaN(parsedPageLimit) && parsedPageLimit > 0) {
    pageSize.value = parsedPageLimit;
  }
}

async function loadSpecialistForms() {
  const response = await scopedApi.form.index({
    license_id: props.licenseId,
    page: page.value,
    page_size: pageSize.value,
  });

  forms.value = response?.data || [];

  const parsedTotalCount = parseInt(response.headers?.['x-total-count'] ?? '', 10);
  const parsedPageSize = parseInt(response.headers?.['x-page-size'] ?? '', 10);

  totalItemCount.value = Number.isNaN(parsedTotalCount) ? forms.value.length : parsedTotalCount;

  if (!Number.isNaN(parsedPageSize) && parsedPageSize > 0) {
    pageSize.value = parsedPageSize;
  }
}

onMounted(load);
</script>
